/**
 * Camera Session — fullscreen camera overlay with multi-photo capture.
 *
 * Opens a fullscreen camera with:
 * - ImageCapture API for full-resolution stills (canvas fallback for iOS)
 * - Fixed 5-slot thumbnail strip
 * - Flanking "Next Device" / "Done" buttons (enabled after 1+ photos)
 * - Gallery button for importing existing photos
 * - Torch toggle for dark environments
 * - Barcode detection on captured images
 * - Save-to-device-gallery
 */

import { showToast } from './toast.js';
import { hapticTap } from './haptics.js';
import { BarcodeDetector as ZXingBarcodeDetector } from 'barcode-detector/pure';

// Use native BarcodeDetector when available; WASM fallback otherwise
const BarcodeDetectorImpl = ('BarcodeDetector' in window)
  ? window.BarcodeDetector
  : ZXingBarcodeDetector;

const BARCODE_FORMATS = ['code_128', 'code_39', 'ean_13', 'qr_code', 'ean_8', 'upc_a', 'code_93'];

// ── State ────────────────────────────────────────────────────────────────────

let sessionState = null;
// {
//   stream: MediaStream,
//   imageCapture: ImageCapture | null,
//   video: HTMLVideoElement,
//   overlay: HTMLElement,
//   photos: Array<{ blob: Blob, thumbUrl: string, barcodes: string[] }>,
//   deviceIndex: number,
//   maxPhotos: number,
//   onComplete: (photos) => void,
//   onCancel: () => void,
//   triggerEl: HTMLElement | null,
//   torchOn: boolean,
//   torchSupported: boolean,
// }

const MAX_PHOTOS = 5;

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Open a fullscreen camera session.
 * @param {{ onComplete: (photos: Array<{blob, thumbUrl, barcodes}>) => void, onCancel: () => void, deviceIndex?: number }} options
 */
export async function openCameraSession({ onComplete, onCancel, deviceIndex = 1 }) {
  if (sessionState) closeCameraSession();

  const triggerEl = document.activeElement;
  const overlay = renderOverlay(deviceIndex);
  document.body.appendChild(overlay);

  sessionState = {
    stream: null,
    imageCapture: null,
    video: overlay.querySelector('.cam-video'),
    overlay,
    photos: [],
    deviceIndex,
    maxPhotos: MAX_PHOTOS,
    onComplete: onComplete || (() => {}),
    onCancel: onCancel || (() => {}),
    triggerEl,
    torchOn: false,
    torchSupported: false,
  };

  // Focus the close button (not shutter — prevents accidental snap on Enter)
  overlay.querySelector('.cam-close-btn').focus();

  // Keyboard handling
  overlay.addEventListener('keydown', onKeyDown);

  try {
    await initCamera();
  } catch {
    showToast('Camera access denied');
    closeCameraSession();
  }
}

/** Close the camera session, tear down stream, remove overlay. */
export function closeCameraSession() {
  if (!sessionState) return;

  // Stop stream
  if (sessionState.stream) {
    sessionState.stream.getTracks().forEach((t) => t.stop());
  }

  // Revoke thumbnail object URLs
  for (const photo of sessionState.photos) {
    if (photo.thumbUrl) URL.revokeObjectURL(photo.thumbUrl);
  }

  // Remove overlay
  sessionState.overlay?.remove();
  sessionState.overlay?.removeEventListener('keydown', onKeyDown);

  // Return focus
  sessionState.triggerEl?.focus();

  sessionState = null;
}

/** Returns the current photo count or 0 if no session. */
export function getSessionPhotoCount() {
  return sessionState?.photos.length ?? 0;
}

// ── Camera Init ──────────────────────────────────────────────────────────────

async function initCamera() {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 4096 },
      height: { ideal: 3072 },
    },
    audio: false,
  });

  sessionState.stream = stream;
  sessionState.video.srcObject = stream;

  // ImageCapture for full-res stills (not available on iOS Safari)
  const track = stream.getVideoTracks()[0];
  if ('ImageCapture' in window) {
    sessionState.imageCapture = new ImageCapture(track);
  }

  // Check torch capability
  try {
    const caps = track.getCapabilities?.();
    if (caps?.torch) {
      sessionState.torchSupported = true;
      sessionState.overlay.querySelector('.cam-torch-btn')?.classList.remove('hidden');
    }
  } catch { /* ignore */ }
}

// ── Photo Capture ────────────────────────────────────────────────────────────

async function capturePhoto() {
  if (!sessionState) return;
  if (sessionState.photos.length >= sessionState.maxPhotos) return;

  hapticTap();
  flashAnimation();

  let blob;
  if (sessionState.imageCapture) {
    try {
      blob = await sessionState.imageCapture.takePhoto();
    } catch {
      // Fallback to canvas
      blob = await captureViaCanvas();
    }
  } else {
    blob = await captureViaCanvas();
  }

  if (blob) addPhotoToSession(blob);
}

async function captureViaCanvas() {
  const video = sessionState.video;
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d').drawImage(video, 0, 0);
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.95);
  });
}

function addPhotoToSession(blob) {
  if (!sessionState) return;

  // Create thumbnail
  const thumbUrl = URL.createObjectURL(blob);
  const photo = { blob, thumbUrl, barcodes: [] };
  sessionState.photos.push(photo);

  // Save full-res to device gallery (fire-and-forget)
  saveToDeviceGallery(blob).catch(() => {});

  // Run barcode detection asynchronously (fire-and-forget)
  detectBarcodesOnImage(blob, sessionState.photos.length - 1);

  // Update UI
  const idx = sessionState.photos.length - 1;
  fillThumbnailSlot(idx, thumbUrl);
  updatePhotoCounter();
  updateActionButtons();

  // Check max
  if (sessionState.photos.length >= sessionState.maxPhotos) {
    const shutterBtn = sessionState.overlay.querySelector('.cam-shutter');
    shutterBtn.disabled = true;
    shutterBtn.setAttribute('aria-label', 'Maximum photos reached');
    const maxLabel = sessionState.overlay.querySelector('.cam-max-label');
    if (maxLabel) maxLabel.classList.remove('hidden');
    navigator.vibrate?.([12, 60, 12]);
  }
}

// ── Barcode Detection ─────────────────────────────────────────────────────────

async function detectBarcodesOnImage(blob, photoIndex) {
  try {
    const detector = new BarcodeDetectorImpl({ formats: BARCODE_FORMATS });
    const bitmap = await createImageBitmap(blob);
    const results = await detector.detect(bitmap);
    bitmap.close();

    if (!sessionState || photoIndex >= sessionState.photos.length) return;

    const values = results.map((r) => r.rawValue).filter(Boolean);
    if (values.length === 0) return;

    sessionState.photos[photoIndex].barcodes = values;

    // Update thumbnail with barcode badge
    const slot = sessionState.overlay?.querySelector(`.cam-thumb-slot[data-slot="${photoIndex}"]`);
    if (slot && !slot.querySelector('.cam-thumb-barcode')) {
      const badge = document.createElement('span');
      badge.className = 'cam-thumb-barcode';
      badge.textContent = values.length;
      badge.setAttribute('aria-label', `${values.length} barcode${values.length > 1 ? 's' : ''} detected`);
      slot.appendChild(badge);
    }
  } catch {
    // Barcode detection is best-effort — never block the camera flow
  }
}

// ── Torch ────────────────────────────────────────────────────────────────────

async function toggleTorch() {
  if (!sessionState?.torchSupported) return;
  const track = sessionState.stream?.getVideoTracks()[0];
  if (!track) return;

  sessionState.torchOn = !sessionState.torchOn;
  try {
    await track.applyConstraints({ advanced: [{ torch: sessionState.torchOn }] });
    const btn = sessionState.overlay.querySelector('.cam-torch-btn');
    btn?.classList.toggle('on', sessionState.torchOn);
  } catch {
    sessionState.torchOn = false;
  }
}

// ── Gallery Import ───────────────────────────────────────────────────────────

function openGallery() {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.multiple = true;
  input.addEventListener('change', (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (!sessionState) break;
      if (sessionState.photos.length >= sessionState.maxPhotos) break;
      addPhotoToSession(file);
    }
  });
  input.click();
}

// ── Save to Device Gallery ────────────────────────────────────────────────────

async function saveToDeviceGallery(blob) {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const filename = `SnipeApp-${timestamp}.jpg`;

  // Fallback: create a download link (works on Android Chrome, iOS Safari)
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

// ── UI Rendering ─────────────────────────────────────────────────────────────

function renderOverlay(deviceIndex) {
  const overlay = document.createElement('div');
  overlay.className = 'camera-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', `Camera — Device ${deviceIndex}`);

  // Build thumbnail slots
  let thumbSlots = '';
  for (let i = 0; i < MAX_PHOTOS; i++) {
    thumbSlots += `<div class="cam-thumb-slot" data-slot="${i}"></div>`;
  }

  overlay.innerHTML = `
    <div class="cam-top-bar">
      <button class="cam-close-btn" aria-label="Close camera" type="button">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <span class="cam-device-label">Device ${deviceIndex}</span>
      <span class="cam-counter" role="status" aria-live="polite">
        <span class="sr-only" id="cam-count-sr">0 of ${MAX_PHOTOS} photos taken</span>
        <span aria-hidden="true" id="cam-count-visual">0/${MAX_PHOTOS}</span>
      </span>
    </div>

    <div class="cam-viewfinder">
      <video class="cam-video" autoplay playsinline muted aria-hidden="true"></video>
      <div class="cam-frame-guide">
        <div class="corner-tl"></div><div class="corner-tr"></div>
        <div class="corner-bl"></div><div class="corner-br"></div>
      </div>
      <button class="cam-torch-btn hidden" aria-label="Toggle flashlight" type="button">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
      </button>
      <div class="cam-flash"></div>
    </div>

    <div class="cam-thumb-strip">${thumbSlots}</div>

    <div class="cam-actions">
      <button class="cam-gallery-btn" aria-label="Import from gallery" type="button">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
      </button>
      <button class="cam-action-btn cam-next-btn" disabled type="button">Next Device</button>
      <button class="cam-shutter" aria-label="Take photo (0 of ${MAX_PHOTOS})" type="button"></button>
      <button class="cam-action-btn cam-done-btn" disabled type="button">Done</button>
      <span class="cam-max-label hidden">Max reached</span>
    </div>
  `;

  // Wire events
  overlay.querySelector('.cam-close-btn').addEventListener('click', () => {
    sessionState?.onCancel();
    closeCameraSession();
  });
  overlay.querySelector('.cam-shutter').addEventListener('click', capturePhoto);
  overlay.querySelector('.cam-torch-btn').addEventListener('click', toggleTorch);
  overlay.querySelector('.cam-gallery-btn').addEventListener('click', openGallery);
  overlay.querySelector('.cam-next-btn').addEventListener('click', onNextDevice);
  overlay.querySelector('.cam-done-btn').addEventListener('click', onDone);

  // Thumbnail slot click → remove photo
  overlay.querySelectorAll('.cam-thumb-slot').forEach((slot) => {
    slot.addEventListener('click', () => {
      const idx = parseInt(slot.dataset.slot, 10);
      if (idx < sessionState?.photos.length) removePhotoFromSession(idx);
    });
  });

  return overlay;
}

// ── UI Updates ───────────────────────────────────────────────────────────────

function fillThumbnailSlot(index, thumbUrl) {
  if (!sessionState) return;
  const slot = sessionState.overlay.querySelector(`.cam-thumb-slot[data-slot="${index}"]`);
  if (!slot) return;
  slot.classList.add('filled');
  slot.innerHTML = `
    <img src="${thumbUrl}" alt="Photo ${index + 1}" />
    <span class="cam-thumb-remove" aria-label="Remove photo ${index + 1}">×</span>
  `;
}

function updatePhotoCounter() {
  if (!sessionState) return;
  const count = sessionState.photos.length;
  const visual = sessionState.overlay.querySelector('#cam-count-visual');
  const sr = sessionState.overlay.querySelector('#cam-count-sr');
  if (visual) visual.textContent = `${count}/${MAX_PHOTOS}`;
  if (sr) sr.textContent = `${count} of ${MAX_PHOTOS} photos taken`;

  const shutter = sessionState.overlay.querySelector('.cam-shutter');
  if (shutter) shutter.setAttribute('aria-label', `Take photo (${count} of ${MAX_PHOTOS})`);
}

function updateActionButtons() {
  if (!sessionState) return;
  const hasPhotos = sessionState.photos.length > 0;
  const nextBtn = sessionState.overlay.querySelector('.cam-next-btn');
  const doneBtn = sessionState.overlay.querySelector('.cam-done-btn');

  if (hasPhotos) {
    nextBtn.disabled = false;
    nextBtn.classList.add('enabled');
    doneBtn.disabled = false;
    doneBtn.classList.add('enabled');
  }
}

function removePhotoFromSession(index) {
  if (!sessionState) return;
  if (index >= sessionState.photos.length) return;

  const photo = sessionState.photos[index];
  if (photo.thumbUrl) URL.revokeObjectURL(photo.thumbUrl);
  sessionState.photos.splice(index, 1);
  navigator.vibrate?.([8, 40, 8]);

  // Rebuild all thumbnail slots
  const slots = sessionState.overlay.querySelectorAll('.cam-thumb-slot');
  slots.forEach((slot, i) => {
    if (i < sessionState.photos.length) {
      slot.classList.add('filled');
      slot.innerHTML = `
        <img src="${sessionState.photos[i].thumbUrl}" alt="Photo ${i + 1}" />
        <span class="cam-thumb-remove" aria-label="Remove photo ${i + 1}">×</span>
      `;
    } else {
      slot.classList.remove('filled');
      slot.innerHTML = '';
    }
  });

  updatePhotoCounter();

  // Re-enable shutter if below max
  if (sessionState.photos.length < sessionState.maxPhotos) {
    const shutter = sessionState.overlay.querySelector('.cam-shutter');
    shutter.disabled = false;
    shutter.setAttribute('aria-label', `Take photo (${sessionState.photos.length} of ${MAX_PHOTOS})`);
    sessionState.overlay.querySelector('.cam-max-label')?.classList.add('hidden');
  }

  // Disable action buttons if no photos
  if (sessionState.photos.length === 0) {
    const nextBtn = sessionState.overlay.querySelector('.cam-next-btn');
    const doneBtn = sessionState.overlay.querySelector('.cam-done-btn');
    nextBtn.disabled = true;
    nextBtn.classList.remove('enabled');
    doneBtn.disabled = true;
    doneBtn.classList.remove('enabled');
  }
}

// ── Flash Animation ──────────────────────────────────────────────────────────

function flashAnimation() {
  if (!sessionState) return;
  const flash = sessionState.overlay.querySelector('.cam-flash');
  if (!flash) return;
  flash.classList.add('active');
  flash.addEventListener('animationend', () => flash.classList.remove('active'), { once: true });
}

// ── Session Actions ──────────────────────────────────────────────────────────

let doneConfirmTimer = null;

function onDone() {
  if (!sessionState || sessionState.photos.length === 0) return;

  const btn = sessionState.overlay.querySelector('.cam-done-btn');
  if (btn.dataset.confirm === '1') {
    // Second tap — confirm
    clearTimeout(doneConfirmTimer);
    btn.dataset.confirm = '';
    btn.textContent = 'Done';
    finishSession('done');
  } else {
    // First tap — show confirm
    btn.dataset.confirm = '1';
    btn.textContent = 'Confirm?';
    doneConfirmTimer = setTimeout(() => {
      if (btn) {
        btn.dataset.confirm = '';
        btn.textContent = 'Done';
      }
    }, 1500);
  }
}

function onNextDevice() {
  if (!sessionState || sessionState.photos.length === 0) return;
  navigator.vibrate?.(20);
  finishSession('next');
}

function finishSession(reason = 'done') {
  if (!sessionState) return;

  // Capture state before cleanup
  const photos = sessionState.photos.map((p) => ({
    blob: p.blob,
    thumbUrl: p.thumbUrl,
    barcodes: [...p.barcodes],
  }));
  const onComplete = sessionState.onComplete;

  navigator.vibrate?.([20, 30, 60]);

  // Stop stream and remove overlay — but don't revoke thumbUrls (caller needs them)
  if (sessionState.stream) {
    sessionState.stream.getTracks().forEach((t) => t.stop());
  }
  sessionState.overlay?.removeEventListener('keydown', onKeyDown);
  sessionState.overlay?.remove();
  sessionState.triggerEl?.focus();
  sessionState = null;

  onComplete(photos, reason);
}

// ── Keyboard ─────────────────────────────────────────────────────────────────

function onKeyDown(e) {
  if (e.key === 'Escape') {
    e.preventDefault();
    sessionState?.onCancel();
    closeCameraSession();
  }

  // Focus trap: Tab cycles within overlay
  if (e.key === 'Tab' && sessionState?.overlay) {
    const focusable = sessionState.overlay.querySelectorAll(
      'button:not([disabled]):not(.hidden), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
